import { useState, useMemo, type ReactNode } from 'react';
import OrgLink from '../components/OrgLink';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Clock, SearchX, FolderKanban, Building2 } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import DataTable, { type CardSpec, type Column, type RowAction } from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import type { Project } from '../types';
import Badge from '../components/ui/Badge';
import FilterBar, { type FilterChip, type FilterDef } from '../components/ui/FilterBar';
import SavedViews, { type SystemView } from '../components/ui/SavedViews';
import { useArrivalView } from '../components/lists/useArrivalView';
import { useIsDesktop } from '../components/ui/useOverlay';
import {
  PROJECT_FILTER_STATUSES, SORT_PATTERN, distinctOptions, filterProjects, isProjectOverdue, pageSlice, parseSort, serializeSort, sortRows, withSelected,
} from '../components/lists/listsModel';
import { canonicalQuery, type ListSchema } from '../components/table/listParams';
import { useListParams, useDebouncedParam } from '../hooks/useListParams';
import { formatDate } from '../utils/format';
import ProjectFormModal from '../components/projects/ProjectFormModal';
import { hoursText } from '../components/projects/projectModel';
import { undoableDeleteProject, DELETE_KINDS } from '../utils/undoableDeletes';
import { useWithoutHidden } from '../utils/undoableActions';
import { useInternalOrgId } from '../hooks/useInternalOrg';
import { CUSTOMERS_GROUP_KEY, INTERNAL_WORK_LABEL_KEY, internalFirstFilterOptions } from '../utils/internalWorkOption';

/** Quiet placeholder for an empty cell. */
function Dash() {
  return <span className="text-text-3" aria-hidden="true">—</span>;
}

type ProjectRow = Project & {
  organization_name: string;
  ticket_count: number;
  open_ticket_count: number;
  total_actual_hours: number;
  overdue_ticket_count: number;
  overdue_days: number | null;
  /** QA-R2/2 — what the caller may do with the row (the server's rule). */
  permissions?: { can_edit: boolean; can_delete: boolean };
};

/**
 * /projects on DataTable v2 (T-42, PRD §6.5; P-04/D, D-39). The projects are
 * loaded once and filtered, sorted and paged here. Columns: name · type ·
 * customer · project manager · status (+ phase) · start · due · progress ·
 * planned / actual hours · lateness. Filters: customer, project manager,
 * status, date range, late only, archived. Search: project, customer or
 * project manager.
 */
const DATE_PARAM = /^\d{4}-\d{2}-\d{2}$/;
const PROJECT_PARAMS = {
  q: { kind: 'string' },
  // P-02c/F-07 — 'cancelled' lists the cancelled projects (hidden otherwise);
  // P-04/D — one status (planning / active / on_hold).
  state: { kind: 'enum', values: ['closed', 'cancelled', ...PROJECT_FILTER_STATUSES] },
  overdue: { kind: 'bool' },
  archived: { kind: 'bool' },
  customer: { kind: 'string', pattern: /^[\w-]+$/ },
  manager: { kind: 'string', pattern: /^[\w-]+$/ },
  from: { kind: 'string', pattern: DATE_PARAM },
  to: { kind: 'string', pattern: DATE_PARAM },
  sort: { kind: 'string', pattern: SORT_PATTERN },
  page: { kind: 'int', default: 1, min: 1 },
} satisfies ListSchema;
const VIEW_KEYS = ['state', 'overdue', 'customer', 'manager'] as const;
const SORTABLE = ['name', 'start_date', 'end_date', 'progress'];
const LIST_ID = 'projects';
const PAGE_SIZE = 50;
const FETCH_LIMIT = 200;

const viewQuery = (v: Partial<Record<(typeof VIEW_KEYS)[number], string | boolean>>) => canonicalQuery(PROJECT_PARAMS, v as never, VIEW_KEYS);

/** Edit from the list: loads the project (permissions, financials) and opens the form. */
function ProjectEditFromList({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['project', projectId], queryFn: () => api.getProject(projectId) });
  const project = data?.data;
  if (!project) return null;
  const kind = (project.kind ?? project.type) === 'development' ? 'development' : 'project';
  return (
    <ProjectFormModal
      mode="edit"
      kind={kind}
      project={project as any}
      canEditCommercial={project.permissions?.can_edit_commercial ?? false}
      showFinancials={project.permissions?.can_view_financials ?? false}
      onClose={onClose}
      onSaved={() => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        onClose();
      }}
    />
  );
}

export default function ProjectsPage() {
  const { t } = useTranslation();
  const internalOrgId = useInternalOrgId();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isStaff = user?.role === 'admin' || user?.role === 'agent';

  const list = useListParams(PROJECT_PARAMS, { pageKey: 'page' });
  const f = list.values;
  useArrivalView(list, VIEW_KEYS, LIST_ID, user?.id);
  const [searchText, setSearchText] = useDebouncedParam(f.q, (q) => list.set({ q }, { replace: true }));

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingProject, setDeletingProject] = useState<ProjectRow | null>(null);

  /** All projects (one request, as before); archived ones only when asked for (P-02a/C3). */
  const { data: projects, isLoading, isError, error: projectsError, refetch } = useQuery({
    queryKey: ['projects', { archived: f.archived, cancelled: f.state === 'cancelled' }],
    queryFn: async () => {
      const res = await api.getProjects({
        limit: FETCH_LIMIT,
        include_archived: f.archived || undefined,
        ...(f.state === 'cancelled' ? { status: 'cancelled' } : {}),
      });
      return (res.data ?? []) as ProjectRow[];
    },
    enabled: isStaff,
    staleTime: 30_000,
  });
  const allRows = useMemo(() => projects ?? [], [projects]);
  // P-27: a project waiting for its delayed DELETE ("בטל") is already out of the list.
  const rows = useWithoutHidden(DELETE_KINDS.project, allRows);

  const sort = useMemo(() => parseSort(f.sort, SORTABLE), [f.sort]);
  const filtered = useMemo(() => filterProjects(rows, f), [rows, f]);
  const sorted = useMemo(() => sortRows(filtered, sort, {
    name: (p) => p.name,
    start_date: (p) => p.start_date,
    end_date: (p) => p.end_date,
    progress: (p) => p.metrics?.progress ?? 0,
  }), [filtered, sort]);
  const slice = pageSlice(sorted, f.page, PAGE_SIZE);

  /* ---------------- Filters and views ---------------- */
  // Customers / managers that have projects (taken from the rows).
  // D-274 §1 — "עבודה פנימית OTE" first, apart from the customers.
  const customerOptions = internalFirstFilterOptions(withSelected(
    distinctOptions(rows, (p) => p.organization_id, (p) => p.organization_name),
    f.customer,
    f.customer && t('projects.list.unknownCustomer'),
  ), internalOrgId, t(INTERNAL_WORK_LABEL_KEY), t(CUSTOMERS_GROUP_KEY));
  const managerOptions = withSelected(
    distinctOptions(rows.filter((p) => !!p.project_manager_id), (p) => p.project_manager_id as string, (p) => p.project_manager_name ?? ''),
    f.manager,
    f.manager && t('projects.list.unknownManager'),
  );
  const filterDefs: FilterDef[] = [
    {
      key: 'state',
      label: t('project.status'),
      value: f.state,
      options: [
        ...PROJECT_FILTER_STATUSES.map((s) => ({ value: s, label: t(`projectStatus.${s}`) })),
        { value: 'closed', label: t('projectStatus.completed') },
        { value: 'cancelled', label: t('projectStatus.cancelled') },
      ],
      allLabel: t('projects.list.viewOpen'),
      onChange: (state) => list.set({ state }),
    },
    { key: 'customer', kind: 'search-select', label: t('project.organization'), value: f.customer, options: customerOptions, allLabel: t('projects.allClients'), onChange: (customer) => list.set({ customer }) },
    { key: 'manager', kind: 'search-select', label: t('projects.list.manager'), value: f.manager, options: managerOptions, allLabel: t('projects.list.allManagers'), onChange: (manager) => list.set({ manager }) },
    {
      key: 'dates',
      kind: 'dateRange',
      label: t('projects.list.dates'),
      from: f.from,
      to: f.to,
      onRangeChange: (patch) => list.set(patch),
    },
    {
      key: 'overdue',
      label: t('projects.list.overdueFilter'),
      value: f.overdue ? 'only' : '',
      options: [{ value: 'only', label: t('projects.list.overdueOnly') }],
      allLabel: t('projects.list.overdueAll'),
      hideChip: true,
      onChange: (v) => list.set({ overdue: v === 'only' }),
    },
    {
      key: 'archived',
      label: t('projects.list.archiveFilter'),
      value: f.archived ? 'show' : '',
      options: [{ value: 'show', label: t('projects.list.showArchived') }],
      allLabel: t('projects.list.hideArchived'),
      hideChip: true,
      onChange: (v) => list.set({ archived: v === 'show' }),
    },
  ];
  const chips: FilterChip[] = [];
  if (f.overdue) chips.push({ key: 'overdue', label: t('projects.list.overdueOnly'), onRemove: () => list.set({ overdue: false }) });
  if (f.archived) chips.push({ key: 'archived', label: t('projects.list.showArchived'), onRemove: () => list.set({ archived: false }) });
  const filtersActive = chips.length > 0 || !!f.customer || !!f.manager || !!f.from || !!f.to || !!f.q;
  const clearAll = () => list.set({ state: '', customer: '', manager: '', from: '', to: '', overdue: false, archived: false, q: '' });

  const systemViews: SystemView[] = [
    { id: 'open', name: t('projects.list.viewOpen'), query: viewQuery({}) },
    { id: 'overdue', name: t('projects.list.viewOverdue'), query: viewQuery({ overdue: true }) },
    { id: 'closed', name: t('projects.list.viewClosed'), query: viewQuery({ state: 'closed' }) },
  ];

  /* ---------------- Rows ---------------- */
  // P-27 (D-166 s.11): hidden at once, DELETE after 6 s, "בטל" cancels it.
  const handleDeleteProject = () => {
    if (!deletingProject) return;
    const target = deletingProject;
    setDeletingProject(null);
    undoableDeleteProject({
      qc: queryClient, id: target.id,
      onError: (err) => toast('error', apiErrorMessage(err, t('projects.deleteError'))),
    });
  };

  // Lateness has one owner: the server's `overdue_days` (only > 0 is late), shown in its own column.
  const overdueLabel = (p: ProjectRow) => t('projects.list.overdueBy', { count: p.overdue_days ?? 0 });
  const overdueBadge = (p: ProjectRow) => (isProjectOverdue(p) ? <Badge tone="danger">{overdueLabel(p)}</Badge> : null);
  const dateCell = (d?: string | null) => (d
    ? <span className="whitespace-nowrap text-xs tabular-nums text-text-2">{formatDate(d)}</span>
    : <Dash />);
  const statusCell = (p: ProjectRow) => (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span title={p.status === 'cancelled' ? (p.cancellation_reason || undefined) : undefined}><StatusBadge status={p.status as any} /></span>
      {p.metrics?.display_phase && <span className="whitespace-nowrap text-xs text-text-2">{t(`projectPhase.${p.metrics.display_phase}`)}</span>}
      {p.archived_at && <Badge>{t('project.archivedBadge')}</Badge>}
    </span>
  );
  const hoursCell = (p: ProjectRow) => {
    const m = p.metrics;
    if (!m) return <Dash />;
    const planned = m.planned_hours === null ? t('projectPage.notDefined') : hoursText(m.planned_hours);
    return <span className="whitespace-nowrap text-xs tabular-nums text-text-2">{planned} / {hoursText(m.actual_hours)}</span>;
  };

  const columns: Column<ProjectRow>[] = [
    {
      key: 'name',
      header: t('project.name'),
      link: true,
      sortable: true,
      // FU-08 — the name keeps a readable minimum width; a longer name wraps
      // to a second line, and its full text is the tooltip.
      className: 'min-w-32',
      render: (p) => <bdi title={p.name} className="line-clamp-2 whitespace-normal break-words">{p.name}</bdi>,
    },
    {
      // P-02b (D-36 Q-P10) — projects and developments, the type in plain text (no colors, D-34).
      key: 'type',
      header: t('project.type'),
      width: 'w-20',
      render: (p) => <span className="whitespace-nowrap text-xs text-text-2">{t(`projectType.${p.kind ?? p.type}`)}</span>,
    },
    {
      key: 'customer',
      header: t('project.organization'),
      priority: 2,
      width: 'w-40',
      // The customer is its own link, above the row link (dt-above).
      render: (p) => (p.organization_name ? (
        <OrgLink
          orgId={p.organization_id}
          name={p.organization_name}
          className="dt-above block max-w-40 truncate text-xs text-text-2 hover:text-accent-text hover:underline underline-offset-2"
        />
      ) : <Dash />),
    },
    {
      key: 'manager',
      header: t('projects.list.manager'),
      priority: 2,
      width: 'w-32',
      render: (p) => (p.project_manager_name ? <span className="block max-w-32 truncate text-xs text-text-2">{p.project_manager_name}</span> : <Dash />),
    },
    { key: 'status', header: t('project.status'), width: 'w-36', render: statusCell },
    { key: 'start_date', header: t('project.startDate'), align: 'end', priority: 3, width: 'w-24', sortable: true, render: (p) => dateCell(p.start_date) },
    { key: 'end_date', header: t('project.endDate'), align: 'end', width: 'w-24', sortable: true, render: (p) => dateCell(p.end_date) },
    {
      key: 'progress',
      header: t('projectPage.progress'),
      align: 'end',
      priority: 2,
      width: 'w-20',
      sortable: true,
      render: (p) => (p.metrics ? <span className="text-xs tabular-nums text-text-2">{p.metrics.progress}%</span> : <Dash />),
    },
    { key: 'hours', header: t('projects.list.hours'), align: 'end', priority: 3, width: 'w-28', render: hoursCell },
    {
      key: 'overdue',
      header: t('projects.overdueColumn'),
      align: 'end',
      priority: 2,
      width: 'w-24',
      render: (p) => (isProjectOverdue(p)
        ? <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-danger-700"><Clock size={12} aria-hidden="true" />{t('projects.overdueDays', { count: Number(p.overdue_days) || 0 })}</span>
        : <Dash />),
    },
  ];

  const card = (p: ProjectRow): CardSpec => ({
    title: <bdi>{p.name}</bdi>,
    end: p.end_date ? dateCell(p.end_date) : undefined,
    badges: (
      <>
        <span title={p.status === 'cancelled' ? (p.cancellation_reason || undefined) : undefined}><StatusBadge status={p.status as any} /></span>
        {p.archived_at && <Badge>{t('project.archivedBadge')}</Badge>}
        {overdueBadge(p)}
      </>
    ),
    metaItems: [
      t(`projectType.${p.kind ?? p.type}`),
      p.organization_name,
      p.project_manager_name ?? '',
      p.metrics ? `${p.metrics.progress}%` : '',
    ].filter(Boolean),
  });

  // QA-R2/2 (D-53) — only what the server allows for the row (`permissions`), never a guess from the role.
  const rowActions = (p: ProjectRow): RowAction[] => [
    ...(p.permissions?.can_edit ? [{ key: 'edit', label: t('projects.list.edit'), icon: <Pencil size={16} strokeWidth={1.75} />, onSelect: () => setEditingId(p.id) }] : []),
    ...(p.permissions?.can_delete ? [{ key: 'delete', label: t('projects.list.delete'), icon: <Trash2 size={16} strokeWidth={1.75} />, danger: true, separatorBefore: p.permissions?.can_edit === true, onSelect: () => setDeletingProject(p) }] : []),
  ];

  const addButton = (
    <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
      <Plus size={16} aria-hidden="true" />
      {t('projects.create')}
    </button>
  );

  if (!isStaff) {
    return (
      <div>
        <PageHeader title={t('projects.title')} description={t('projects.description')} />
        <EmptyState title={t('projects.noAccessTitle')} description={t('projects.noAccessDescription')} />
      </div>
    );
  }

  let summary: ReactNode = null;
  if (!isLoading && !isError) {
    const parts = [t('projects.list.count', { count: filtered.length })];
    if (rows.length >= FETCH_LIMIT) parts.push(t('projects.list.capped', { count: FETCH_LIMIT }));
    summary = parts.join(' · ');
  }

  const closed = f.state === 'closed';
  const empty = filtersActive ? (
    <EmptyState
      icon={SearchX}
      title={t('uxLists.noMatchesTitle')}
      description={t('uxLists.noMatchesDesc')}
      action={<button type="button" className="btn btn-secondary" onClick={clearAll}>{t('common.clearFilter')}</button>}
    />
  ) : (
    <EmptyState
      icon={FolderKanban}
      title={closed ? t('projects.noClosed') : t('projects.noResults')}
      description={closed ? undefined : t('uxLists.projectsEmptyDesc')}
      action={closed ? undefined : addButton}
    />
  );

  return (
    <div>
      <PageHeader title={t('projects.title')} description={t('projects.description')} actions={addButton} />
      {/* D-321 - a fixed, separate entry to the company's own work (not a customer: not counted, not filtered). */}
      <Link
        to="/internal-work"
        data-testid="internal-hub-link"
        className="mb-3 inline-flex items-center gap-2 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2 text-sm font-medium text-surface-800 hover:bg-surface-100"
      >
        <Building2 size={16} aria-hidden="true" /> {t('internalHub.link')}
      </Link>

      <FilterBar
        className="mb-4"
        leading={(
          <SavedViews
            listId={LIST_ID}
            systemViews={systemViews}
            currentQuery={list.query(VIEW_KEYS)}
            onApply={(q) => list.applyView(q, VIEW_KEYS)}
          />
        )}
        search={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={t('projects.list.searchPlaceholder')}
        filters={filterDefs}
        chips={chips}
        onClearAll={clearAll}
        summary={summary}
      />

      {/* T-11 — the same behaviour as the task lists (AllTasksPage,
          TaskRowsTable): from md up DataTable's own sideways scroll
          (mobile="scroll"): no column or ⋯ is clipped, the project name keeps a
          readable width and stays sticky, no sticky header and no inner
          vertical scroll box. Phones keep the cards. */}
      <DataTable
        columns={columns}
        data={slice.rows}
        keyField="id"
        caption={t('projects.list.caption')}
        mobile={isDesktop ? 'scroll' : 'cards'}
        getRowHref={(p) => `/projects/${p.id}`}
        rowLabel={(p) => p.name}
        card={card}
        rowActions={rowActions}
        sort={sort}
        onSortChange={(next) => list.set({ sort: serializeSort(next) }, { replace: true })}
        isLoading={isLoading}
        skeletonRows={8}
        error={isError ? projectsError ?? true : undefined}
        errorMessage={isError ? apiErrorMessage(projectsError, t('common.loadFailed')) : undefined}
        onRetry={() => { void refetch(); }}
        emptyState={empty}
        pagination={!isLoading && !isError && slice.pageCount > 1 ? {
          page: slice.page,
          pageCount: slice.pageCount,
          total: slice.total,
          onChange: (p) => list.set({ page: p }),
        } : undefined}
      />

      {/* P-04/B — "פרויקט חדש": an active customer is picked (D-30); then the project page. */}
      {showCreate && (
        <ProjectFormModal
          mode="create"
          kind="project"
          onClose={() => setShowCreate(false)}
          onSaved={(id) => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            navigate(`/projects/${id}`);
          }}
        />
      )}
      {editingId && <ProjectEditFromList projectId={editingId} onClose={() => setEditingId(null)} />}
      {deletingProject && (
        <ConfirmDeleteModal
          title={t('projects.deleteTitle')}
          itemName={deletingProject.name}
          description={t('projects.deleteWarning')}
          onConfirm={handleDeleteProject}
          onCancel={() => setDeletingProject(null)}
        />
      )}
    </div>
  );
}
